import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "react-loader-spinner";
import Choose from "../chooseItems/index.jsx";
import ProductContextMenu from "./ProductContextMenu.jsx";
import LayoutEditBar from "./LayoutEditBar.jsx";
import SortableProductGrid from "./SortableProductGrid.jsx";
import {
  moveDraftItem,
  saveCategoryLayout,
  selectDisplayedProducts,
  selectEditing,
  selectLayoutStatus,
  startEditing,
  stopEditing,
} from "../../../store/slices/layoutSlice";
import { WR } from "../../../components/ui/tokens";

export const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill,minmax(176px,1fr))",
  gap: 14,
};

const NOTICE_MS = 4000;

// The product grid of ONE subcategory on the sale screen (Home renders one per
// subcategory and hides the others). Owns everything about the per-shop
// layout: the context menu, the drag-and-drop edit mode and its toolbar.
// Renders nothing while its subcategory is not the selected one.
const ProductGrid = ({ categoryId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedSubcategoryId = useSelector((state) => state.cart.selectedSubcategoryId);
  const error = useSelector((state) => state.cart.error);
  const products = useSelector(selectDisplayedProducts);
  const editing = useSelector(selectEditing);
  const status = useSelector(selectLayoutStatus);

  const [menu, setMenu] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const active = selectedSubcategoryId === categoryId;
  const isEditingHere = active && !!editing && String(editing.categoryId) === String(categoryId);

  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(id);
  }, [notice]);

  const openMenu = useCallback((point) => setMenu(point), []);
  const closeMenu = useCallback(() => setMenu(null), []);

  if (!active) return null;

  const beginEditing = () => {
    setMenu(null);
    setSaveError(null);
    dispatch(startEditing({ categoryId, draft: products.map((product) => product.id) }));
  };

  const persist = (productIds) =>
    dispatch(saveCategoryLayout({ categoryId, productIds }))
      .unwrap()
      .then((result) => {
        setSaveError(null);
        dispatch(stopEditing());
        if (result.persistedTo === "local") setNotice(t("LAYOUT_SAVED_LOCAL"));
      })
      .catch(() => setSaveError(t("LAYOUT_SAVE_ERROR")));

  const handleValidate = () => persist(editing.draft);
  const handleCancel = () => {
    setSaveError(null);
    dispatch(stopEditing());
  };
  const handleReset = () => {
    if (window.confirm(t("LAYOUT_RESET_CONFIRM"))) persist([]);
  };

  let content;
  if (error) {
    content = <p>{typeof error === "string" ? error : error?.error}</p>;
  } else if (products.length > 0) {
    content = isEditingHere ? (
      <SortableProductGrid
        products={products}
        gridStyle={GRID_STYLE}
        onReorder={(move) => dispatch(moveDraftItem(move))}
      />
    ) : (
      <div style={GRID_STYLE}>
        {products.map((product) => (
          <Choose key={product.id} data={product} onOpenMenu={openMenu} />
        ))}
      </div>
    );
  } else {
    content = (
      <div className="d-flex justify-content-center" style={{ width: "100%", marginTop: "15rem" }}>
        <Loader type="Oval" color="#34A6AC" height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      {isEditingHere && (
        <LayoutEditBar
          saving={status === "saving"}
          error={saveError}
          onValidate={handleValidate}
          onCancel={handleCancel}
          onReset={handleReset}
        />
      )}
      {notice && (
        <div
          role="status"
          style={{
            marginBottom: 14,
            padding: "10px 14px",
            borderRadius: WR.radius.md,
            background: WR.color.mintBg,
            color: WR.color.mintDeep,
            fontSize: 13,
            fontWeight: 700,
            fontFamily: WR.font.sans,
          }}
        >
          {notice}
        </div>
      )}
      {content}
      {menu && !editing && (
        <ProductContextMenu position={menu} onEdit={beginEditing} onClose={closeMenu} />
      )}
    </div>
  );
};

export default ProductGrid;
